import type { AbstractIntlMessages } from 'next-intl'

/**
 * How long a failed catalog load stays the answer. React renders a
 * suspended tree again as soon as its load settles: dropping the failure at
 * once handed that render a fresh load, so a catalog that kept failing
 * (offline, a deploy that dropped it) was fetched again and again, back to
 * back, and the error boundary never showed.
 */
export const FAILED_LOAD_KEPT_MS = 10_000

/**
 * Starts `load` once per `key` and hands every caller the same promise, so
 * `use` gets the same one back on every render. A failed load stays the
 * error of the trees that asked (their boundary shows its fallback); a
 * render once `FAILED_LOAD_KEPT_MS` has passed (the boundary's retry, a
 * remount) loads it again.
 */
export function sharedCatalogLoad(
  loads: Map<string, Promise<AbstractIntlMessages>>,
  key: string,
  load: () => Promise<AbstractIntlMessages>
): Promise<AbstractIntlMessages> {
  const cached = loads.get(key)
  if (cached) {
    return cached
  }
  const started = load()
  started.catch(() => {
    setTimeout(() => {
      if (loads.get(key) === started) {
        loads.delete(key)
      }
    }, FAILED_LOAD_KEPT_MS)
  })
  loads.set(key, started)
  return started
}
